import React, { FC } from 'react'
import { Paragraph, TitleController } from '@pandiafi/design-system'
import { useTranslation } from 'react-i18next'
import {
  ResidentAppBackLink,
  ResidentAppCard,
  ResidentAppView,
} from '@pandiafi/components'

import { DefectNoticeSuccess } from '../Success'
import { ErrorHandler } from '../DefectNoticeErrorHandler'
import { EditDefectNoticeDataLoader } from './DefectNoticeEditViewDataLoader'
import { DefectNoticeEvents } from './components/DefectNoticeEvents/DefectNoticeEvents'
import { DefectNoticeInfo } from './components/DefectNoticeInfo/DefectNoticeInfo'
import { DefectNoticeForm } from '../Form'

import { useContractPaths } from 'hooks/useContractPaths/useContractPaths'

export type DefectNoticeViewState = 'form' | 'preview' | 'success'

export const DefectNoticeEditView: FC = () => {
  const [view, setView] = React.useState<DefectNoticeViewState>('form')
  const { t } = useTranslation(['common', 'defectNotice', 'titles'])
  const { defectNoticePath } = useContractPaths()

  return (
    <TitleController title={t('titles:defectNotice')}>
      <ResidentAppView
        title={t('defectNotice:headings.defectNoticeEdit')}
        backLink={
          <ResidentAppBackLink href={defectNoticePath}>
            {t('common:views.defectNotice')}
          </ResidentAppBackLink>
        }
      >
        <ResidentAppCard>
          <ErrorHandler>
            <EditDefectNoticeDataLoader>
              {({ defectNotice, ...data }) => {
                // A notice that a Kenno user has changed to an observation or moved
                // to another location stays visible to the resident, but core no
                // longer accepts updates to it, so the form is left out entirely.
                const isEditable =
                  defectNotice.isTrackable &&
                  defectNotice.noticeType === 'defect_notice'
                const readOnlyText = !defectNotice.isTrackable
                  ? t('defectNotice:texts.notTrackable')
                  : !isEditable
                    ? t('defectNotice:texts.notEditable')
                    : null

                return (
                  <>
                    {view === 'success' && <DefectNoticeSuccess editMode />}
                    {view === 'form' && (
                      <>
                        <DefectNoticeInfo defectNotice={defectNotice} />
                        {readOnlyText && (
                          <Paragraph className="mt-6">{readOnlyText}</Paragraph>
                        )}
                        <DefectNoticeEvents events={defectNotice.events} />
                      </>
                    )}
                    {view !== 'success' && isEditable && (
                      <DefectNoticeForm
                        {...data}
                        defectNotice={defectNotice}
                        view={view}
                        setView={setView}
                      />
                    )}
                  </>
                )
              }}
            </EditDefectNoticeDataLoader>
          </ErrorHandler>
        </ResidentAppCard>
      </ResidentAppView>
    </TitleController>
  )
}
